import { AxiosError } from 'axios';
import constate from 'constate';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InfiniteData,
  useInfiniteQuery,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { useLocation } from 'react-router-dom';

import { useAuthUser } from 'auth/hooks';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useIsDocumentVisible } from 'core/hooks';
import { NOTIFICATIONS_QUERY_KEY } from 'notifications/constants';
import { BellNotificationListFilter } from 'notifications/enums';
import * as notificationService from 'notifications/services/notification';
import {
  IBellNotification,
  IBellNotificationsResponse,
  IBellNotificationsUnreadSummary,
} from 'notifications/types';
import {
  countUnreadNotificationsIn,
  getHighestNotificationId,
  getUniqueNotifications,
  markNotificationPagesRead,
  markNotificationPagesUnread,
  removeNotificationPages,
} from 'notifications/utils/notification';
import { isIframePath } from 'router/utils/iframe';

const POLL_INTERVAL_MS = 45000;
const PAGE_SIZE = 20;

const LIST_QUERY_KEY = 'list';
const SUMMARY_QUERY_KEY = 'unread-count';
const WRITE_MUTATION_KEY = [NOTIFICATIONS_QUERY_KEY, 'write'];

type TNotificationPages = InfiniteData<IBellNotificationsResponse>;

interface IWriteContext {
  list?: TNotificationPages;
  summary?: IBellNotificationsUnreadSummary;
  unreadList?: TNotificationPages;
}

type TUpdatePages = (data: TNotificationPages) => TNotificationPages;

const keepPages: TUpdatePages = (data) => data;

interface IPendingArrival {
  afterId: number;
  untilId: number;
}

// A 4xx (expired session, missing ACL, endpoint not deployed) will not fix itself, and the BE
// logs every one of them, so the poll stops until the next login or page load.
const isClientError = (error: AxiosError) => {
  const status = error?.response?.status;

  return status >= 400 && status < 500;
};

const getNextPageParam = (
  lastPage: IBellNotificationsResponse,
  pages: IBellNotificationsResponse[]
) => (pages.length * PAGE_SIZE < (lastPage?.total ?? 0) ? pages.length + 1 : undefined);

const useBellNotifications = () => {
  const [blockedScope, setBlockedScope] = useState<string | null>(null);
  const [newArrivals, setNewArrivals] = useState<IBellNotification[]>([]);
  // While open, the panel lists everything: no arrival cards are planned.
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [listFilter, setListFilter] = useState(BellNotificationListFilter.ALL);
  const lastSummaryRef = useRef<IBellNotificationsUnreadSummary | null>(null);
  const highestSeenIdRef = useRef<number | null>(null);
  const pendingArrivalRef = useRef<IPendingArrival | null>(null);
  const pendingWritesRef = useRef(0);

  const { t } = useTranslation();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  const { user } = useAuthUser();
  const isDocumentVisible = useIsDocumentVisible();
  const isWriting = useIsMutating({ mutationKey: WRITE_MUTATION_KEY }) > 0;
  const { errors } = useHandleErrors({
    error: { title: t('errorTitleBellNotificationUpdate') },
    position: NotificationPosition.TOP,
  });

  const scope = user ? `${user.id}-${user.companyId}` : null;
  const listKey = useMemo(() => [NOTIFICATIONS_QUERY_KEY, LIST_QUERY_KEY, scope], [scope]);
  const summaryKey = useMemo(() => [NOTIFICATIONS_QUERY_KEY, SUMMARY_QUERY_KEY, scope], [scope]);
  // Under the list key: invalidating the list refreshes the Unread tab too.
  const unreadListKey = useMemo(() => [...listKey, BellNotificationListFilter.UNREAD], [listKey]);
  // Queries pause while a write is in flight so a refetch cannot resurrect an optimistic change.
  const isEnabled = !!scope && !isIframePath(pathname) && blockedScope !== scope && !isWriting;

  const onFetchError = useCallback(
    (error: AxiosError) => {
      if (isClientError(error)) {
        setBlockedScope(scope);
      }
    },
    [scope]
  );

  // react-query v3 also fires onSuccess for setQueryData, so the optimistic updates and
  // rollbacks of a write in flight must not be read as server changes.
  const onSummaryFetched = useCallback(
    (summary: IBellNotificationsUnreadSummary) => {
      if (pendingWritesRef.current) {
        return;
      }

      const previous = lastSummaryRef.current;
      const latestId = summary?.latestId ?? 0;

      lastSummaryRef.current = summary;

      if (!previous || highestSeenIdRef.current === null) {
        highestSeenIdRef.current = latestId;

        return;
      }

      if (latestId > highestSeenIdRef.current) {
        pendingArrivalRef.current = {
          afterId: pendingArrivalRef.current?.afterId ?? highestSeenIdRef.current,
          untilId: latestId,
        };
        highestSeenIdRef.current = latestId;
      }

      // The newest arrival was dismissed before the list caught up: wait for what is left of it.
      const pendingArrival = pendingArrivalRef.current;

      if (pendingArrival && latestId < pendingArrival.untilId) {
        pendingArrivalRef.current =
          latestId > pendingArrival.afterId ? { ...pendingArrival, untilId: latestId } : null;
      }

      if (
        previous.latestId !== summary?.latestId ||
        previous.unreadCount !== summary?.unreadCount
      ) {
        // An in-flight list fetch may predate a new arrival; restart it instead of reusing it.
        queryClient.invalidateQueries(listKey, undefined, {
          cancelRefetch: !!pendingArrivalRef.current,
        });
      }
    },
    [listKey, queryClient]
  );

  const onListFetched = useCallback((data: TNotificationPages) => {
    const pendingArrival = pendingArrivalRef.current;

    if (!pendingArrival || pendingWritesRef.current) {
      return;
    }

    const fetched = getUniqueNotifications(data);

    // A response older than the poll that raised latest_id; the next list fetch brings it.
    if (!fetched.some((notification) => notification.id >= pendingArrival.untilId)) {
      return;
    }

    pendingArrivalRef.current = null;

    const arrivals = fetched.filter(
      (notification) => notification.id > pendingArrival.afterId && !notification.isRead
    );

    if (arrivals.length) {
      setNewArrivals(arrivals);
    }
  }, []);

  const { data: summary } = useQuery(summaryKey, notificationService.getUnreadSummary, {
    enabled: isEnabled && isDocumentVisible,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
    onSuccess: onSummaryFetched,
    onError: onFetchError,
  });

  const {
    data: list,
    isError,
    isFetching,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    listKey,
    ({ pageParam = 1 }) =>
      notificationService.getNotifications({ page: pageParam, perPage: PAGE_SIZE }),
    {
      enabled: isEnabled,
      staleTime: Infinity,
      retry: false,
      getNextPageParam,
      onSuccess: onListFetched,
      onError: onFetchError,
    }
  );

  const isUnreadFilter = BellNotificationListFilter.UNREAD === listFilter;

  // Fetched only while the drawer shows it; the arrivals are read off the All list alone.
  const {
    data: unreadList,
    isError: isUnreadError,
    isFetching: isUnreadFetching,
    isLoading: isUnreadLoading,
    hasNextPage: hasNextUnreadPage,
    isFetchingNextPage: isFetchingNextUnreadPage,
    fetchNextPage: fetchNextUnreadPage,
  } = useInfiniteQuery(
    unreadListKey,
    ({ pageParam = 1 }) =>
      notificationService.getNotifications({
        page: pageParam,
        perPage: PAGE_SIZE,
        unreadOnly: true,
      }),
    {
      enabled: isEnabled && isPanelOpen && isUnreadFilter,
      staleTime: Infinity,
      retry: false,
      getNextPageParam,
      onError: onFetchError,
    }
  );

  const notifications = useMemo(() => getUniqueNotifications(list), [list]);
  const unreadNotifications = useMemo(() => getUniqueNotifications(unreadList), [unreadList]);
  const lastPage = list?.pages?.[list.pages.length - 1];
  const unreadCount = summary?.unreadCount ?? lastPage?.unreadCount ?? 0;
  const total = lastPage?.total ?? 0;

  const takeSnapshot = useCallback(async (): Promise<IWriteContext> => {
    pendingWritesRef.current += 1;

    await queryClient.cancelQueries([NOTIFICATIONS_QUERY_KEY]);

    return {
      list: queryClient.getQueryData<TNotificationPages>(listKey),
      summary: queryClient.getQueryData<IBellNotificationsUnreadSummary>(summaryKey),
      unreadList: queryClient.getQueryData<TNotificationPages>(unreadListKey),
    };
  }, [listKey, queryClient, summaryKey, unreadListKey]);

  const applyOptimisticUpdate = useCallback(
    (
      context: IWriteContext,
      updateList: TUpdatePages,
      updateUnreadList: TUpdatePages,
      unreadDelta: number | null
    ) => {
      if (context.list) {
        queryClient.setQueryData(listKey, updateList(context.list));
      }

      if (context.unreadList) {
        queryClient.setQueryData(unreadListKey, updateUnreadList(context.unreadList));
      }

      if (context.summary) {
        queryClient.setQueryData(summaryKey, {
          ...context.summary,
          unreadCount:
            null === unreadDelta ? 0 : Math.max(0, context.summary.unreadCount - unreadDelta),
        });
      }
    },
    [listKey, queryClient, summaryKey, unreadListKey]
  );

  const writeOptions = useMemo(
    () => ({
      ...errors,
      mutationKey: WRITE_MUTATION_KEY,
      onError: (error: AxiosError, variables: number, context: IWriteContext) => {
        if (context?.list) {
          queryClient.setQueryData(listKey, context.list);
        }

        if (context?.summary) {
          queryClient.setQueryData(summaryKey, context.summary);
        }

        if (context?.unreadList) {
          queryClient.setQueryData(unreadListKey, context.unreadList);
        }

        errors.onError(error, variables);
      },
      onSettled: () => {
        pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
        queryClient.invalidateQueries([NOTIFICATIONS_QUERY_KEY]);
      },
    }),
    [errors, listKey, queryClient, summaryKey, unreadListKey]
  );

  const { mutate: markReadMutation } = useMutation(notificationService.markAsRead, {
    ...writeOptions,
    onMutate: async (id: number) => {
      const context = await takeSnapshot();
      const unreadDelta = countUnreadNotificationsIn([context.list, context.unreadList], [id]);

      // A read notification leaves the Unread tab at once instead of after the refetch.
      applyOptimisticUpdate(
        context,
        (data) => markNotificationPagesRead(data, [id], new Date().toISOString()),
        (data) => removeNotificationPages(data, [id]),
        unreadDelta
      );

      return context;
    },
  });

  const { mutate: markUnreadMutation } = useMutation(notificationService.markAsUnread, {
    ...writeOptions,
    onMutate: async (id: number) => {
      const context = await takeSnapshot();
      const wasRead = getUniqueNotifications(context.list).some(
        (notification) => notification.id === id && notification.isRead
      );

      applyOptimisticUpdate(
        context,
        (data) => markNotificationPagesUnread(data, id),
        keepPages,
        wasRead ? -1 : 0
      );

      return context;
    },
  });

  const { mutate: markAllReadMutation } = useMutation(notificationService.markAllAsRead, {
    ...writeOptions,
    onMutate: async () => {
      const context = await takeSnapshot();

      applyOptimisticUpdate(
        context,
        (data) => markNotificationPagesRead(data, null, new Date().toISOString()),
        (data) => removeNotificationPages(data, null),
        null
      );

      return context;
    },
  });

  const { mutate: dismissMutation } = useMutation(notificationService.dismiss, {
    ...writeOptions,
    onMutate: async (id: number) => {
      const context = await takeSnapshot();
      const unreadDelta = countUnreadNotificationsIn([context.list, context.unreadList], [id]);
      const removeDismissed: TUpdatePages = (data) => removeNotificationPages(data, [id]);

      applyOptimisticUpdate(context, removeDismissed, removeDismissed, unreadDelta);

      return context;
    },
  });

  const { mutate: dismissAllMutation } = useMutation(notificationService.dismissAll, {
    ...writeOptions,
    onMutate: async () => {
      const context = await takeSnapshot();

      const removeAll: TUpdatePages = (data) => removeNotificationPages(data, null);

      applyOptimisticUpdate(context, removeAll, removeAll, null);

      return context;
    },
  });

  const markRead = useCallback(
    (id: number) => {
      const notification = notifications.find((item) => item.id === id);

      if (notification?.isRead) {
        return;
      }

      markReadMutation(id);
    },
    [markReadMutation, notifications]
  );

  const markUnread = useCallback(
    (id: number) => {
      const notification = notifications.find((item) => item.id === id);

      if (!notification?.isRead) {
        return;
      }

      markUnreadMutation(id);
    },
    [markUnreadMutation, notifications]
  );

  // The summary too: a stale unread count would otherwise outlive the list it describes.
  const refresh = useCallback(() => {
    queryClient.invalidateQueries(summaryKey);
    queryClient.invalidateQueries(listKey);
  }, [listKey, queryClient, summaryKey]);

  // Both "all" writes stop at the newest notification rendered here (either tab), so one that
  // arrived since the last refresh is left alone. Nothing rendered = no bound: reload instead of an
  // unbounded write.
  const renderedMaxId = useMemo(
    () => getHighestNotificationId([...notifications, ...unreadNotifications]),
    [notifications, unreadNotifications]
  );

  const markAllRead = useCallback(() => {
    if (!unreadCount) {
      return;
    }

    const maxId = renderedMaxId;

    if (null === maxId) {
      refresh();

      return;
    }

    markAllReadMutation(maxId);
  }, [markAllReadMutation, refresh, renderedMaxId, unreadCount]);

  const dismiss = useCallback((id: number) => dismissMutation(id), [dismissMutation]);

  const dismissAll = useCallback(() => {
    const maxId = renderedMaxId;

    if (null === maxId) {
      refresh();

      return;
    }

    dismissAllMutation(maxId);
  }, [dismissAllMutation, refresh, renderedMaxId]);

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const loadMoreUnread = useCallback(() => {
    if (!hasNextUnreadPage || isFetchingNextUnreadPage) {
      return;
    }

    fetchNextUnreadPage();
  }, [fetchNextUnreadPage, hasNextUnreadPage, isFetchingNextUnreadPage]);

  // react-query v3 refetches every cached page, one after the other, on each invalidation, so
  // the pages loaded with "Load more" are dropped once the panel closes.
  const collapsePages = useCallback(() => {
    [listKey, unreadListKey].forEach((key) => {
      const data = queryClient.getQueryData<TNotificationPages>(key);

      if (!data?.pages || data.pages.length < 2) {
        return;
      }

      queryClient.setQueryData<TNotificationPages>(key, {
        pages: data.pages.slice(0, 1),
        pageParams: data.pageParams.slice(0, 1),
      });
    });
  }, [listKey, queryClient, unreadListKey]);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);

  const closePanel = useCallback(() => setIsPanelOpen(false), []);

  const togglePanel = useCallback(() => setIsPanelOpen((prev) => !prev), []);

  // Every opening starts on All.
  useEffect(() => {
    if (!isPanelOpen) {
      setListFilter(BellNotificationListFilter.ALL);
    }
  }, [isPanelOpen]);

  // The list the drawer shows: the All list is also the toasts' source, the Unread one is not.
  const drawerList = useMemo(
    () =>
      isUnreadFilter
        ? {
            notifications: unreadNotifications,
            hasMore: !!hasNextUnreadPage,
            isError: isUnreadError,
            isFetching: isUnreadFetching,
            isFetchingMore: isFetchingNextUnreadPage,
            // The query is still idle for the render that enables it: without this the drawer
            // flashes the empty state before the first fetch starts.
            isLoading: isUnreadLoading || (!unreadList && !isUnreadError),
            loadMore: loadMoreUnread,
          }
        : {
            notifications,
            hasMore: !!hasNextPage,
            isError,
            isFetching,
            isFetchingMore: isFetchingNextPage,
            isLoading,
            loadMore,
          },
    [
      hasNextPage,
      hasNextUnreadPage,
      isError,
      isFetching,
      isFetchingNextPage,
      isFetchingNextUnreadPage,
      isLoading,
      isUnreadError,
      isUnreadFetching,
      isUnreadFilter,
      isUnreadLoading,
      loadMore,
      loadMoreUnread,
      notifications,
      unreadList,
      unreadNotifications,
    ]
  );

  useEffect(() => {
    lastSummaryRef.current = null;
    highestSeenIdRef.current = null;
    pendingArrivalRef.current = null;
    setBlockedScope(null);
    setNewArrivals([]);
  }, [scope]);

  return useMemo(
    () => ({
      closePanel,
      collapsePages,
      dismiss,
      dismissAll,
      drawerList,
      hasMore: !!hasNextPage,
      isError,
      isFetching,
      isFetchingMore: isFetchingNextPage,
      isLoading,
      isPanelOpen,
      listFilter,
      loadMore,
      markAllRead,
      markRead,
      markUnread,
      newArrivals,
      notifications,
      openPanel,
      refresh,
      setListFilter,
      togglePanel,
      total,
      unreadCount,
    }),
    [
      closePanel,
      collapsePages,
      dismiss,
      dismissAll,
      drawerList,
      hasNextPage,
      isError,
      isFetching,
      isFetchingNextPage,
      isLoading,
      isPanelOpen,
      listFilter,
      loadMore,
      markAllRead,
      markRead,
      markUnread,
      newArrivals,
      notifications,
      openPanel,
      refresh,
      togglePanel,
      total,
      unreadCount,
    ]
  );
};

export const [BellNotificationsProvider, useBellNotificationsContext] =
  constate(useBellNotifications);
